import {MISC_KEYS, useOnboardingCrawlingMutation} from "@/api/miscApi";
import OnboardingLoader from "@/components/onboarding/OnboardingLoader";
import OnboardingStepContainer from "@/components/onboarding/OnboardingStepContainer";
import type {CrawledBrandDataSerializer} from "@/generated/DjangoTypes";
import {useSSE} from "@/hooks/useSSE";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {
  isFinishRegistrationModalVisibleAtom,
  isInvalidCompanyUrlModalOpenAtom,
  languagePreferenceAtom,
} from "@/store/jotaiAtoms";
import {useOnboardingStore} from "@/store/onboarding";
import {createFileRoute} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {useSetAtom} from "jotai";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/_unauthenticated/onboarding")({
  component: Onboarding,
  validateSearch: zodValidator(
    z.object({
      access_token: z.string().optional(),
      campaign_prompt: z.string().optional(),
      campaign_type: z.string().optional(),
      country: z.string().optional(),
      domain: z.string().optional(),
      email: z.string().optional(),
      language: z.string().optional(),
      step: z.number().optional(),
      user_id: z.number().optional(),
    }),
  ),
});

function Onboarding() {
  const {t} = useTranslation("common");

  useTitle(t("registerCompany"));

  const navigate = Route.useNavigate();
  const {campaign_prompt, campaign_type, domain, email, language, step} = Route.useSearch();
  const crawlingFailed = useOnboardingStore(state => state.crawlingFailed);
  const sessionId = useOnboardingStore(state => state.sessionId);
  const setter = useOnboardingStore(state => state.setter);
  const setIsFinishRegistrationModalVisible = useSetAtom(isFinishRegistrationModalVisibleAtom);
  const setLanguagePreference = useSetAtom(languagePreferenceAtom);
  const setIsInvalidCompanyUrlModalOpen = useSetAtom(isInvalidCompanyUrlModalOpenAtom);
  const isOnboardingStepContainerShown = !!step && !crawlingFailed;

  const {mutate: crawlDomain} = useOnboardingCrawlingMutation({domain: domain!});

  const {data: crawledBrandData} = useSSE<CrawledBrandDataSerializer>({
    initialState: {},
    queryKey: MISC_KEYS.qCrawlingStream(sessionId!),
    enabled: !!sessionId,
    onError: () => {
      setter(state => {
        state.crawlingFailed = true;
      });
      setIsInvalidCompanyUrlModalOpen(true);
      capturePostHogEvent(PostHogEvents.OnboardingWebsiteCrawlFailed, {domain});
    },
    onSuccess: () => {
      setter(state => {
        state.crawlingFailed = false;
      });
      capturePostHogEvent(PostHogEvents.OnboardingWebsiteCrawlCompleted, {domain});
    },
  });

  useEffect(() => {
    if (email) {
      setIsFinishRegistrationModalVisible(false);
      setter(state => {
        state.formState.email = email;
      });
      if (!domain && !step) {
        navigate({search: prev => ({...prev, step: 2})});
      }
    }
  }, [email, domain, step, setter, navigate, setIsFinishRegistrationModalVisible]);
  useEffect(() => {
    if (domain) {
      capturePostHogEvent(PostHogEvents.OnboardingStarted, {domain});
      crawlDomain();
    }
  }, [domain, crawlDomain]);
  useEffect(() => {
    if (campaign_prompt || campaign_type) {
      const data = {
        mainPrompt: campaign_prompt ?? "",
        hints: [],
        campaignType: campaign_type ?? "tiktok",
      };
      localStorage.setItem("ai-campaign-creation", JSON.stringify(data));
    }
  }, [campaign_prompt, campaign_type]);
  useEffect(() => {
    if (!!language) {
      const languageLower = language.toLowerCase();
      setLanguagePreference(languageLower === "de" ? "de_CH" : "en_EN");
    }
  }, [language, setLanguagePreference]);
  useEffect(() => {
    if (!!crawledBrandData) {
      setTimeout(() => {
        setter(state => {
          if (typeof crawledBrandData.company_title !== "undefined") {
            if (!state.formState.companyName) {
              state.formState.companyName = crawledBrandData.company_title ?? "";
            }

            if (!state.formState.companyLogo) {
              state.formState.companyLogo = crawledBrandData.company_logo_url!;
            }

            if (!step) {
              navigate({search: prev => ({...prev, step: 1})});
            }
          }

          if (typeof crawledBrandData.company_overview !== "undefined" && !state.formState.companyOverview) {
            state.formState.companyOverview = crawledBrandData.company_overview ?? "";
          }

          if (typeof crawledBrandData.company_bio !== "undefined" && !state.formState.bio) {
            state.formState.bio = crawledBrandData.company_bio ?? "";
          }

          if (typeof crawledBrandData.products_overview !== "undefined" && !state.formState.productsOverview) {
            state.formState.productsOverview = crawledBrandData.products_overview ?? "";
          }

          if (typeof crawledBrandData.customer_pain_points !== "undefined" && !state.formState.customerPainPoints) {
            state.formState.customerPainPoints = crawledBrandData.customer_pain_points ?? "";
          }

          if (crawledBrandData.interests?.length && !state.formState.interests.length) {
            state.formState.interests = crawledBrandData.interests;
          }
        });
      }, 6000);
    }
  }, [crawledBrandData, setter, step]);

  return (
    <div className="flex h-screen justify-center bg-linear-(--onboarding-gradient)">
      {isOnboardingStepContainerShown ? <OnboardingStepContainer /> : <OnboardingLoader />}
    </div>
  );
}
